import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Plus, Table2, Trash2, Loader2, Save, ArrowLeft, BarChart3, LineChart as LineIcon, AreaChart as AreaIcon, PieChart as PieIcon, Columns3, Rows3, FileDown, Copy, FolderPlus, Mail, CheckCircle2, XCircle, Clock, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { colToLetter, letterToCol, computeSheet, displayValue } from "../../lib/sheetEngine";

const PALETTE = ["#c9a961", "#1e3d3a", "#6b7c3a", "#a3823f", "#3b6ea5", "#b23a48", "#2f7d5b", "#8a6d2f"];

// Quote sheet (kind === "quote") well-known cells, used by the smart toolbar.
const QUOTE_PLANS = [{ row: 6, label: "Reset" }, { row: 7, label: "Momentum" }, { row: 8, label: "Elevate" }];
const QUOTE_SETUPS = [
  { val: "0", label: "No setup" },
  { val: "11", label: "Focused Assessment" },
  { val: "12", label: "Expanded Assessment" },
  { val: "13", label: "CRM / Dashboard Setup" },
];
const parseEngagement = (formula) => {
  const m = /^=([BC])(\d+)/.exec((formula || "").trim().toUpperCase());
  return { col: m ? m[1] : "B", row: m ? parseInt(m[2], 10) : 7 };
};
const parseSetup = (formula) => {
  const m = /^=B(11|12|13)\b/.exec((formula || "").trim().toUpperCase());
  if (m) return m[1];
  return (!formula || String(formula).trim() === "0" || String(formula).trim() === "") ? "0" : "custom";
};

const parseRange = (ref) => {
  const m = /^([A-Z]+)(\d+)(?::([A-Z]+)(\d+))?$/.exec((ref || "").trim().toUpperCase());
  if (!m) return [];
  const c1 = letterToCol(m[1]), r1 = parseInt(m[2], 10);
  const c2 = m[3] ? letterToCol(m[3]) : c1, r2 = m[4] ? parseInt(m[4], 10) : r1;
  const addrs = [];
  for (let r = Math.min(r1, r2); r <= Math.max(r1, r2); r++)
    for (let c = Math.min(c1, c2); c <= Math.max(c1, c2); c++)
      addrs.push(colToLetter(c) + r);
  return addrs;
};

function ChartView({ chart, computed }) {
  const labels = parseRange(chart.labels).map((a) => displayValue(computed[a]) || a);
  const vals = parseRange(chart.values).map((a) => Number(computed[a]) || 0);
  const rows = labels.map((name, i) => ({ name, value: vals[i] || 0 }));
  const common = { data: rows, margin: { top: 8, right: 12, left: -12, bottom: 4 } };
  return (
    <div className="rounded-xl border border-[#1e3d3a]/10 bg-white p-3" data-testid={`ws-chart-${chart.id}`}>
      <div className="text-[13px] font-medium text-[#1e3d3a] mb-2">{chart.title || "Chart"}</div>
      <ResponsiveContainer width="100%" height={220}>
        {chart.type === "line" ? (
          <LineChart {...common}><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Line type="monotone" dataKey="value" stroke="#c9a961" strokeWidth={2} dot={false} /></LineChart>
        ) : chart.type === "area" ? (
          <AreaChart {...common}><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Area type="monotone" dataKey="value" stroke="#c9a961" fill="#c9a961" fillOpacity={0.25} /></AreaChart>
        ) : chart.type === "pie" ? (
          <PieChart><Tooltip /><Legend /><Pie data={rows} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>{rows.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}</Pie></PieChart>
        ) : (
          <BarChart {...common}><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="value" radius={[4, 4, 0, 0]}>{rows.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}</Bar></BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

function QuoteToolbar({ data, computed, rows, cols, charts, apiUrl, setCell, sheetId }) {
  const [busy, setBusy] = useState("");
  const [status, setStatus] = useState(null);
  const { col, row } = parseEngagement(data.B27);
  const mode = col === "C" ? "published" : "proposed";
  const setupVal = parseSetup(data.B30);
  const [discount, setDiscount] = useState(data.B32 ?? "");
  const num = (a) => { const v = computed[a]; return typeof v === "number" ? v : Number(v) || 0; };

  const loadStatus = useCallback(async () => {
    try {
      const { data: s } = await api.get(apiUrl(`/sheets/${sheetId}/quote-status`));
      setStatus(s);
    } catch { /* ignore */ }
  }, [apiUrl, sheetId]);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const setPlan = (newRow) => setCell("B27", `=${col}${newRow}`);
  const setMode = (newCol) => setCell("B27", `=${newCol}${row}`);
  const setSetup = (val) => setCell("B30", val === "0" ? "0" : `=B${val}`);
  const commitDiscount = () => setCell("B32", String(discount || "").trim() === "" ? "0" : String(discount).trim());

  const gatherPayload = (businessName, extra = {}) => {
    const planLabel = String(computed["A" + row] || "Engagement");
    const addonLines = [];
    for (let r = 16; r <= 23; r++) {
      const qty = num("D" + r);
      if (qty > 0) addonLines.push({ name: String(computed["A" + r] || "Add-on"), qty, rate: num("B" + r), total: num("E" + r) });
    }
    return {
      business_name: businessName, plan_label: planLabel, price_mode: mode === "published" ? "Published" : "Proposed",
      monthly_price: num("B27"), months: num("B28"), monthly_subtotal: num("B29"),
      one_time_setup: num("B30"), addons_total: num("B31"), addon_lines: addonLines,
      discount: num("B32"), total: num("B33"), sheet_id: sheetId, ...extra,
    };
  };

  const askName = () => (window.prompt("Client / business name for this quote:", status?.business_name || "") || "").trim();

  const downloadPdf = async () => {
    const businessName = askName();
    if (!businessName) return;
    setBusy("pdf");
    try {
      const { data: blob } = await api.post(apiUrl("/quote-pdf"), gatherPayload(businessName), { responseType: "blob" });
      const u = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = u; a.download = `Elevare Quote - ${businessName}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
      toast.success("Quote PDF downloaded.");
    } catch { toast.error("Couldn't build the quote PDF."); }
    finally { setBusy(""); }
  };

  const saveToDocuments = async () => {
    const businessName = askName();
    if (!businessName) return;
    setBusy("docs");
    try {
      const { data: res } = await api.post(apiUrl("/quote-pdf/save"), gatherPayload(businessName));
      toast.success(`Filed in Workspace → Files → Quotes as "${res.filename}".`);
      loadStatus();
    } catch { toast.error("Couldn't file the quote."); }
    finally { setBusy(""); }
  };

  const emailQuote = async () => {
    const businessName = askName();
    if (!businessName) return;
    const to = (window.prompt(`Email this quote to (client's email):`, status?.to_email || "") || "").trim();
    if (!to) return;
    setBusy("email");
    try {
      await api.post(apiUrl("/quote-pdf/email"), gatherPayload(businessName, { to_email: to }));
      toast.success(`Quote emailed to ${to} with a one-click Accept link.`);
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't email the quote.");
    } finally { setBusy(""); }
  };

  const saveCopy = async () => {
    const nm = askName();
    if (!nm) return;
    setBusy("copy");
    try {
      await api.post(apiUrl("/sheets"), { name: `Quote — ${nm}`, kind: "quote", data, rows, cols, charts });
      toast.success(`Saved a copy for ${nm}. Open it from the Sheets list.`);
    } catch { toast.error("Couldn't save the client copy."); }
    finally { setBusy(""); }
  };

  const createInvoice = async () => {
    setBusy("invoice");
    try {
      const { data: res } = await api.post(apiUrl(`/sheets/${sheetId}/invoice`));
      toast.success(res.already
        ? `Draft invoice ${res.invoice_number} is already filed in Workspace → Files → Invoices.`
        : `Draft invoice ${res.invoice_number} created in Workspace → Files → Invoices.`);
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create the draft invoice.");
    } finally { setBusy(""); }
  };

  const btn = "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium disabled:opacity-50 transition-colors";
  const spin = (k, Icon) => (busy === k ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />);
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; } };

  return (
    <div className="mb-4 rounded-xl border border-[#c9a961]/40 bg-[#c9a961]/5 p-3" data-testid="ws-quote-toolbar">
      {status?.status && (
        <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="quote-status-row">
          {status.status === "accepted" && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#2f7d5b]/12 border border-[#2f7d5b]/30 px-3 py-1 text-sm font-medium text-[#1f5a40]" data-testid="quote-status-badge">
              <CheckCircle2 className="h-4 w-4" /> Accepted{status.accepted_name ? ` by ${status.accepted_name}` : ""}{status.accepted_at ? ` · ${fmtDate(status.accepted_at)}` : ""}
            </span>
          )}
          {status.status === "declined" && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#b23a48]/10 border border-[#b23a48]/30 px-3 py-1 text-sm font-medium text-[#8f2230]" data-testid="quote-status-badge"
              title={status.decline_reason || ""}>
              <XCircle className="h-4 w-4" /> Declined{status.declined_at ? ` · ${fmtDate(status.declined_at)}` : ""}
            </span>
          )}
          {status.status === "sent" && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a]/8 border border-[#1e3d3a]/20 px-3 py-1 text-sm font-medium text-[#1e3d3a]/70" data-testid="quote-status-badge">
              <Clock className="h-4 w-4" /> Sent{status.business_name ? ` to ${status.business_name}` : ""} · awaiting response
            </span>
          )}
          {status.status === "declined" && status.decline_reason && (
            <span className="text-xs text-[#8f2230]/80 italic" data-testid="quote-decline-reason">“{status.decline_reason}”</span>
          )}
          {status.invoice_filename && (
            <span className="inline-flex items-center gap-1 text-xs text-[#1e3d3a]/50" data-testid="quote-invoice-note">
              <FileText className="h-3.5 w-3.5" /> {status.invoice_number}
            </span>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Engagement plan</label>
          <select value={row} onChange={(e) => setPlan(Number(e.target.value))} data-testid="quote-plan-select"
            className="rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-1.5 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none">
            {QUOTE_PLANS.map((p) => <option key={p.row} value={p.row}>{p.label}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Price basis</label>
          <div className="inline-flex rounded-lg border border-[#1e3d3a]/15 bg-white p-0.5" data-testid="quote-price-toggle">
            <button onClick={() => setMode("B")} data-testid="quote-mode-proposed"
              className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${mode === "proposed" ? "bg-[#1e3d3a] text-white" : "text-[#1e3d3a]/60 hover:text-[#1e3d3a]"}`}>Proposed</button>
            <button onClick={() => setMode("C")} data-testid="quote-mode-published"
              className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${mode === "published" ? "bg-[#1e3d3a] text-white" : "text-[#1e3d3a]/60 hover:text-[#1e3d3a]"}`}>Published</button>
          </div>
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">One-time setup</label>
          <select value={setupVal} onChange={(e) => setSetup(e.target.value)} data-testid="quote-setup-select"
            className="rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-1.5 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none">
            {QUOTE_SETUPS.map((s) => <option key={s.val} value={s.val}>{s.label}</option>)}
            {setupVal === "custom" && <option value="custom">Custom (edit in grid)</option>}
          </select>
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Discount / deposit</label>
          <div className="inline-flex items-center rounded-lg border border-[#1e3d3a]/15 bg-white px-2">
            <span className="text-sm text-[#1e3d3a]/40">$</span>
            <input type="number" min="0" value={discount} onChange={(e) => setDiscount(e.target.value)}
              onBlur={commitDiscount} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              data-testid="quote-discount-input" placeholder="0"
              className="w-20 px-1 py-1.5 text-sm text-[#1e3d3a] focus:outline-none tabular-nums" />
          </div>
        </div>

        <div className="min-w-[120px]">
          <label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Quote total</label>
          <div className="text-lg font-head text-[#1e3d3a] tabular-nums" data-testid="quote-total">
            {typeof computed.B33 === "number" ? "$" + Math.round(computed.B33).toLocaleString() : displayValue(computed.B33)}
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[#c9a961]/25 pt-3">
        <button onClick={saveCopy} disabled={!!busy} data-testid="quote-save-copy"
          className={`${btn} bg-white border border-[#1e3d3a]/15 text-[#1e3d3a] hover:border-[#c9a961]`}>{spin("copy", Copy)} Save client copy</button>
        <button onClick={saveToDocuments} disabled={!!busy} data-testid="quote-save-docs"
          className={`${btn} bg-white border border-[#1e3d3a]/15 text-[#1e3d3a] hover:border-[#c9a961]`}>{spin("docs", FolderPlus)} Save to Documents</button>
        <button onClick={emailQuote} disabled={!!busy} data-testid="quote-email"
          className={`${btn} bg-white border border-[#1e3d3a]/15 text-[#1e3d3a] hover:border-[#c9a961]`}>{spin("email", Mail)} Email quote</button>
        {status?.status === "accepted" && (
          <button onClick={createInvoice} disabled={!!busy} data-testid="quote-create-invoice"
            className={`${btn} bg-[#1e3d3a] text-white hover:bg-[#2a534d]`}>{spin("invoice", FileText)} {status.invoice_filename ? "Open draft invoice" : "Create draft invoice"}</button>
        )}
        <button onClick={downloadPdf} disabled={!!busy} data-testid="quote-download-pdf"
          className={`${btn} ml-auto bg-[#c9a961] text-[#1e3d3a] font-semibold hover:bg-[#b89550]`}>{spin("pdf", FileDown)} Quote PDF</button>
      </div>
      <p className="mt-2 text-[11px] text-[#1e3d3a]/45">Pick the plan, price basis and setup to auto-fill the quote. Email a branded PDF with a one-click Accept link — you'll see the status here and can draft an invoice once it's accepted.</p>
    </div>
  );
}

function SheetEditor({ apiBase, q, sheetId, onBack }) {
  const [sheet, setSheet] = useState(null);
  const [data, setData] = useState({});
  const [rows, setRows] = useState(50);
  const [cols, setCols] = useState(12);
  const [name, setName] = useState("");
  const [charts, setCharts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const [chartForm, setChartForm] = useState({ title: "", type: "bar", labels: "", values: "" });
  const gridRef = useRef(null);

  const url = (p) => `${apiBase}${p}${q}`;

  // Living document: grow the grid whenever the user scrolls near an edge.
  const onGridScroll = useCallback((e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) setRows((r) => r + 25);
    if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 120) setCols((c) => c + 4);
  }, []);

  useEffect(() => {
    api.get(url(`/sheets/${sheetId}`)).then(({ data: s }) => {
      setSheet(s); setData(s.data || {}); setRows(s.rows || 50); setCols(s.cols || 12); setName(s.name || ""); setCharts(s.charts || []);
    }).catch(() => toast.error("Couldn't open sheet."));
  }, [sheetId]); // eslint-disable-line

  const computed = useMemo(() => computeSheet(data, rows, cols), [data, rows, cols]);

  const save = useCallback(async (extra = {}) => {
    setSaving(true);
    try {
      await api.put(url(`/sheets/${sheetId}`), { name, data, rows, cols, charts, ...extra });
      setDirty(false);
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  }, [name, data, rows, cols, charts, sheetId, apiBase, q]);

  const setCell = useCallback((addr, value) => {
    setData((d) => { const nd = { ...d, [addr]: value }; save({ data: nd }); return nd; });
  }, [save]);

  const commit = () => {    if (editing == null) return;
    setData((d) => { const nd = { ...d }; if (editVal === "") delete nd[editing]; else nd[editing] = editVal; return nd; });
    // Living document: auto-extend when a cell near the bottom/right edge is filled.
    const m = /^([A-Z]+)(\d+)$/.exec(editing);
    if (m && editVal !== "") {
      const rr = parseInt(m[2], 10), cc = letterToCol(m[1]);
      if (rr >= rows) setRows(rr + 15);
      if (cc >= cols - 1) setCols(cc + 4);
    }
    setEditing(null); setEditVal(""); setDirty(true);
  };

  const addChart = () => {
    if (!chartForm.labels || !chartForm.values) { toast.error("Enter both label and value ranges (e.g. A1:A6)."); return; }
    const c = { id: `c${Date.now().toString(36)}`, ...chartForm };
    const next = [...charts, c];
    setCharts(next); setShowChart(false);
    setChartForm({ title: "", type: "bar", labels: "", values: "" });
    save({ charts: next });
    toast.success("Chart added.");
  };
  const removeChart = (id) => { const next = charts.filter((c) => c.id !== id); setCharts(next); save({ charts: next }); };

  if (!sheet) return <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-[#c9a961]" /></div>;

  return (
    <div data-testid="ws-sheet-editor">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <button onClick={onBack} className="inline-flex items-center gap-1 text-[13px] text-[#1e3d3a]/60 hover:text-[#a3823f]"><ArrowLeft className="h-4 w-4" /> Sheets</button>
        <input value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }} onBlur={() => dirty && save()}
          className="font-head text-lg text-[#1e3d3a] bg-transparent border-b border-transparent hover:border-[#1e3d3a]/20 focus:border-[#c9a961] focus:outline-none px-1" data-testid="ws-sheet-name" />
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => { setRows((r) => r + 20); setDirty(true); }} className="inline-flex items-center gap-1 rounded-full bg-[#1e3d3a]/6 px-2.5 py-1 text-xs text-[#1e3d3a]/70 hover:bg-[#1e3d3a]/10"><Rows3 className="h-3.5 w-3.5" /> +Rows</button>
          <button onClick={() => { setCols((c) => c + 4); setDirty(true); }} className="inline-flex items-center gap-1 rounded-full bg-[#1e3d3a]/6 px-2.5 py-1 text-xs text-[#1e3d3a]/70 hover:bg-[#1e3d3a]/10"><Columns3 className="h-3.5 w-3.5" /> +Cols</button>
          <button onClick={() => setShowChart((v) => !v)} data-testid="ws-add-chart-toggle" className="inline-flex items-center gap-1 rounded-full bg-[#c9a961]/15 px-3 py-1 text-xs font-medium text-[#8a6d2f] hover:bg-[#c9a961]/25"><BarChart3 className="h-3.5 w-3.5" /> Chart</button>
          <button onClick={() => save()} disabled={saving} data-testid="ws-sheet-save" className="inline-flex items-center gap-1 rounded-full bg-[#1e3d3a] px-3 py-1 text-xs font-semibold text-white hover:bg-[#2a534d] disabled:opacity-50">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} {dirty ? "Save" : "Saved"}</button>
        </div>
      </div>

      {sheet.kind === "quote" && (
        <QuoteToolbar data={data} computed={computed} rows={rows} cols={cols} charts={charts}
          apiUrl={url} setCell={setCell} sheetId={sheetId} />
      )}

      {showChart && (
        <div className="mb-4 rounded-xl border border-[#c9a961]/40 bg-[#c9a961]/5 p-3" data-testid="ws-chart-form">
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-end">
            <div><label className="text-[10px] uppercase tracking-wide text-[#1e3d3a]/50">Title</label><input value={chartForm.title} onChange={(e) => setChartForm({ ...chartForm, title: e.target.value })} className="w-full rounded-lg border border-[#1e3d3a]/15 px-2 py-1.5 text-sm" placeholder="Monthly revenue" /></div>
            <div><label className="text-[10px] uppercase tracking-wide text-[#1e3d3a]/50">Type</label>
              <select value={chartForm.type} onChange={(e) => setChartForm({ ...chartForm, type: e.target.value })} className="w-full rounded-lg border border-[#1e3d3a]/15 px-2 py-1.5 text-sm bg-white" data-testid="ws-chart-type">
                <option value="bar">Bar</option><option value="line">Line</option><option value="area">Area</option><option value="pie">Pie</option>
              </select></div>
            <div><label className="text-[10px] uppercase tracking-wide text-[#1e3d3a]/50">Labels range</label><input value={chartForm.labels} onChange={(e) => setChartForm({ ...chartForm, labels: e.target.value })} className="w-full rounded-lg border border-[#1e3d3a]/15 px-2 py-1.5 text-sm" placeholder="A1:A6" data-testid="ws-chart-labels" /></div>
            <div><label className="text-[10px] uppercase tracking-wide text-[#1e3d3a]/50">Values range</label><input value={chartForm.values} onChange={(e) => setChartForm({ ...chartForm, values: e.target.value })} className="w-full rounded-lg border border-[#1e3d3a]/15 px-2 py-1.5 text-sm" placeholder="B1:B6" data-testid="ws-chart-values" /></div>
            <button onClick={addChart} data-testid="ws-chart-create" className="rounded-full bg-[#1e3d3a] px-3 py-2 text-sm font-semibold text-white hover:bg-[#2a534d]">Add chart</button>
          </div>
        </div>
      )}

      {/* Grid */}
      <div ref={gridRef} onScroll={onGridScroll} className="overflow-auto rounded-xl border border-[#1e3d3a]/12 bg-white max-h-[70vh]">
        <table className="border-collapse text-sm" data-testid="ws-grid">
          <thead>
            <tr>
              <th className="sticky top-0 left-0 z-20 bg-[#f4f1e9] border border-[#1e3d3a]/10 w-10" />
              {Array.from({ length: cols }).map((_, c) => (
                <th key={c} className="sticky top-0 z-10 bg-[#f4f1e9] border border-[#1e3d3a]/10 px-2 py-1 text-[11px] font-semibold text-[#1e3d3a]/60 min-w-[90px]">{colToLetter(c)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }).map((_, r) => {
              const row = r + 1;
              return (
                <tr key={row}>
                  <td className="sticky left-0 z-10 bg-[#f4f1e9] border border-[#1e3d3a]/10 px-1 text-center text-[11px] text-[#1e3d3a]/50">{row}</td>
                  {Array.from({ length: cols }).map((_, c) => {
                    const addr = colToLetter(c) + row;
                    const isEd = editing === addr;
                    const val = computed[addr];
                    const isNum = typeof val === "number";
                    return (
                      <td key={c} className="border border-[#1e3d3a]/8 p-0">
                        {isEd ? (
                          <input
                            autoFocus
                            value={editVal}
                            onChange={(e) => setEditVal(e.target.value)}
                            onBlur={commit}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } if (e.key === "Escape") { setEditing(null); setEditVal(""); } }}
                            className="w-full px-2 py-1 text-sm outline-none bg-[#c9a961]/10 min-w-[90px]"
                            data-testid={`ws-cell-input-${addr}`}
                          />
                        ) : (
                          <div
                            onClick={() => { setEditing(addr); setEditVal(data[addr] ?? ""); }}
                            className={`px-2 py-1 min-h-[28px] cursor-cell truncate ${isNum ? "text-right tabular-nums" : ""} ${String(val).startsWith?.("#") ? "text-[#b23a48]" : "text-[#1e3d3a]"}`}
                            data-testid={`ws-cell-${addr}`}
                            title={data[addr] || ""}
                          >
                            {displayValue(val)}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-1.5 text-[11px] text-[#1e3d3a]/40">A living document — scroll to the edge or fill the last row/column and it keeps extending on its own. Type values or formulas like <code className="bg-[#1e3d3a]/6 px-1 rounded">=SUM(B1:B6)</code> or <code className="bg-[#1e3d3a]/6 px-1 rounded">=A1*1.08</code>. Press Enter to confirm.</p>

      {charts.length > 0 && (
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {charts.map((c) => (
            <div key={c.id} className="relative">
              <button onClick={() => removeChart(c.id)} className="absolute -top-2 -right-2 z-10 rounded-full bg-white border border-[#1e3d3a]/15 p-1 text-[#1e3d3a]/40 hover:text-[#b23a48] shadow-sm" data-testid={`ws-chart-remove-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              <ChartView chart={c} computed={computed} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function WorkspaceSheets({ apiBase, q = "" }) {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const url = (p) => `${apiBase}${p}${q}`;

  const load = useCallback(async () => {
    try { const { data } = await api.get(url("/sheets")); setSheets(data.sheets || []); }
    catch { toast.error("Couldn't load sheets."); }
    finally { setLoading(false); }
  }, [apiBase, q]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const name = window.prompt("Name your spreadsheet:", "Untitled sheet");
    if (name === null) return;
    try { const { data } = await api.post(url("/sheets"), { name }); setOpenId(data.id); }
    catch { toast.error("Couldn't create sheet."); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this spreadsheet?")) return;
    try { await api.delete(url(`/sheets/${id}`)); await load(); }
    catch { toast.error("Couldn't delete."); }
  };

  if (openId) return <SheetEditor apiBase={apiBase} q={q} sheetId={openId} onBack={() => { setOpenId(null); load(); }} />;
  if (loading) return <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-[#c9a961]" /></div>;

  return (
    <div data-testid="workspace-sheets">
      <div className="flex items-center justify-between mb-4">
        <p className="text-[13px] text-[#1e3d3a]/60">Track data with Excel-style formulas, then turn any range into a chart.</p>
        <button onClick={create} data-testid="ws-new-sheet" className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] px-3.5 py-1.5 text-sm font-semibold text-white hover:bg-[#2a534d]"><Plus className="h-4 w-4" /> New sheet</button>
      </div>
      {sheets.length === 0 ? (
        <div className="py-14 text-center text-sm text-[#1e3d3a]/45 rounded-xl border border-dashed border-[#1e3d3a]/15">No spreadsheets yet. Create your first one.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {sheets.map((s) => (
            <div key={s.id} data-testid={`ws-sheet-card-${s.id}`} className="group rounded-xl border border-[#1e3d3a]/12 bg-white p-4 hover:border-[#c9a961] transition-colors cursor-pointer" onClick={() => setOpenId(s.id)}>
              <div className="flex items-start justify-between">
                <Table2 className="h-6 w-6 text-[#c9a961]" />
                <button onClick={(e) => { e.stopPropagation(); del(s.id); }} className="opacity-0 group-hover:opacity-100 p-1 text-[#1e3d3a]/40 hover:text-[#b23a48]"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="mt-3 font-medium text-[#1e3d3a] truncate">{s.name}</div>
              <div className="text-[11px] text-[#1e3d3a]/45 mt-0.5">{s.chart_count > 0 ? `${s.chart_count} chart${s.chart_count === 1 ? "" : "s"} · ` : ""}{s.rows}×{s.cols}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
